import { useEffect, useState } from "react";
import { Spinner } from "@/components/ui/spinner";
import { SelectList, SelectListItem } from "@/components/ui/select-list";
import { Item, ItemContent, ItemDescription, ItemTitle, ItemList } from "@/components/ui/item";
import { useAuth } from "@clerk/react";
import { CopyIcon, EllipsisIcon, MergeIcon, SplitIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useEveActionsStore } from "@/stores/eve/eveActionsStore";
import { canonicalPipeline, evePhaseLabel, evePhaseOrder } from "@/lib/eve";
import { cn } from "@/lib/utils";
import { Campaign } from "@/types/types";
import { EveTierPipeline } from "@/types/eve";
import { tierOfferLabel } from "./shared";

/**
 * Per-tier pipeline actions for multi-tier campaigns: the tier offer label,
 * the ⋯ menu on each nav group / overview row, and the two dialogs behind
 * it. Both dialogs write through `copy_from_tier` — the executor refuses
 * when a creator on a target tier sits in a phase the copy would remove,
 * and that refusal is toasted verbatim (the dialog stays open).
 */

/** "Contract → Script → Payment" — the enabled phases of one tier, in order. */
function flowSummary(phases: EveTierPipeline["phases"]): string {
  const enabled = [...phases]
    .filter((p) => p.enabled === true)
    .sort((a, b) => evePhaseOrder(a) - evePhaseOrder(b));
  return enabled.length > 0 ? enabled.map((p) => evePhaseLabel(p, phases)).join(" → ") : "No enabled phases";
}

function TierOption({ campaign, group, note }: { campaign: Campaign; group: EveTierPipeline; note?: string }) {
  const tier = campaign.partnership?.tiers?.find((t) => t.order === group.tier);
  const offer = tierOfferLabel(tier, campaign.currency);
  return (
    <ItemContent className="min-w-0 gap-0">
      <ItemTitle className="truncate">
        Tier {group.tier}
        {offer && <span className="font-normal text-muted-foreground">· {offer}</span>}
      </ItemTitle>
      <ItemDescription>{note ?? flowSummary(group.phases)}</ItemDescription>
    </ItemContent>
  );
}

/** copy_from_tier: replace each target tier's pipeline with a copy of `from`'s. */
function useCopyTier(campaignId: string) {
  const { getToken, orgId } = useAuth();
  const { updateCampaignPhases } = useEveActionsStore();
  return async (from: number, targets: number[]): Promise<boolean> => {
    const token = await getToken();
    if (!token || !orgId) {
      console.error("No token found");
      return false;
    }
    return updateCampaignPhases(campaignId, null, orgId, token, { tiers: targets, copy_from_tier: from });
  };
}

interface TierMenuProps {
  /** null = the shared pipeline (every tier identical, one nav group). */
  tier: number | null;
  /** Menu heading — "Tier 2 · CPA $50" or "All 3 tiers". */
  heading: string;
  onSplit: () => void;
  onCopy: (tier: number) => void;
  onUseForAll: (tier: number) => void;
  disabled?: boolean;
  className?: string;
}

/** The ⋯ menu of one tier (or of the shared pipeline). */
export function TierMenu({ tier, heading, onSplit, onCopy, onUseForAll, disabled, className }: TierMenuProps) {
  return (
    // Non-modal: the items open dialogs, and a modal menu closing under an
    // opening dialog can leave the page's pointer-events locked.
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          disabled={disabled}
          aria-label={`${heading} options`}
          className={cn(
            "size-6 shrink-0 text-muted-foreground hover:bg-muted-foreground/10 data-[state=open]:bg-muted-foreground/10",
            className
          )}
        >
          <EllipsisIcon className="size-3.5" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-52">
        <DropdownMenuLabel className="text-xs font-normal text-muted-foreground truncate">{heading}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {tier == null ? (
          <DropdownMenuItem onSelect={onSplit}>
            <SplitIcon />
            Split into one per tier
          </DropdownMenuItem>
        ) : (
          <>
            <DropdownMenuItem onSelect={() => onCopy(tier)}>
              <CopyIcon />
              Copy to other tiers…
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => onUseForAll(tier)}>
              <MergeIcon />
              Use for all tiers…
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** Copy one tier's pipeline onto the tiers the user ticks. Open while `from` is set. */
export function CopyTierDialog({
  campaign,
  groups,
  from,
  onClose,
}: {
  campaign: Campaign;
  groups: EveTierPipeline[];
  from: number | null;
  onClose: () => void;
}) {
  const { isSavingConfig } = useEveActionsStore();
  const copyTo = useCopyTier(campaign._id);
  const [targets, setTargets] = useState<number[]>([]);
  useEffect(() => {
    if (from == null) setTargets([]);
  }, [from]);

  const source = groups.find((g) => g.tier === from);
  const sourceKey = source ? canonicalPipeline(source.phases) : "";

  const run = async () => {
    if (from == null) return;
    if (await copyTo(from, targets)) onClose();
  };

  return (
    <AlertDialog open={from != null} onOpenChange={(open) => !open && onClose()}>
      <AlertDialogContent className="max-w-lg">
        <AlertDialogHeader>
          <AlertDialogTitle>Copy Tier {from}'s pipeline</AlertDialogTitle>
          <AlertDialogDescription>
            The tiers you pick are replaced with Tier {from}'s pipeline
            {source ? ` (${flowSummary(source.phases)})` : ""}. Amy refuses while a creator on one of them
            sits in a phase the copy would remove.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <ItemList>
          {groups
            .filter((g) => g.tier !== from)
            .map((g) => {
              const same = canonicalPipeline(g.phases) === sourceKey;
              return (
                <Item key={g.tier} variant="row" asChild>
                  <label className="has-disabled:cursor-not-allowed has-disabled:opacity-60">
                  <Checkbox
                    checked={targets.includes(g.tier)}
                    disabled={same || isSavingConfig}
                    onCheckedChange={(value) =>
                      setTargets((prev) =>
                        value === true ? [...prev, g.tier].sort((a, b) => a - b) : prev.filter((t) => t !== g.tier)
                      )
                    }
                  />
                  <TierOption campaign={campaign} group={g} note={same ? "Already the same" : undefined} />
                  </label>
                </Item>
              );
            })}
        </ItemList>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <Button disabled={targets.length === 0 || isSavingConfig} onClick={run}>
            {isSavingConfig && <Spinner />}
            Copy pipeline
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

/**
 * Collapse differing tiers into one shared pipeline: pick the tier every
 * tier keeps (preselected to `source`), the others get a copy of it. Open
 * while `source` is set.
 */
export function ShareTierDialog({
  campaign,
  groups,
  source,
  onClose,
  onShared,
}: {
  campaign: Campaign;
  groups: EveTierPipeline[];
  source: number | null;
  onClose: () => void;
  onShared: () => void;
}) {
  const { isSavingConfig } = useEveActionsStore();
  const copyTo = useCopyTier(campaign._id);
  const [picked, setPicked] = useState(source ?? groups[0]?.tier ?? 1);
  useEffect(() => {
    if (source != null) setPicked(source);
  }, [source]);

  const run = async () => {
    const others = groups.map((g) => g.tier).filter((t) => t !== picked);
    if (await copyTo(picked, others)) {
      onClose();
      onShared();
    }
  };

  return (
    <AlertDialog open={source != null} onOpenChange={(open) => !open && onClose()}>
      <AlertDialogContent className="max-w-lg">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <MergeIcon className="size-4 text-muted-foreground" />
            One pipeline for all tiers
          </AlertDialogTitle>
          <AlertDialogDescription>
            Pick the pipeline every tier should run — the other tiers are replaced with a copy of it.
            Amy refuses while a creator on another tier sits in a phase the copy would remove.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <SelectList
          value={String(picked)}
          onValueChange={(v) => setPicked(Number(v))}
          disabled={isSavingConfig}
        >
          {groups.map((g) => (
            <SelectListItem key={g.tier} value={String(g.tier)}>
              <TierOption campaign={campaign} group={g} />
            </SelectListItem>
          ))}
        </SelectList>
        <AlertDialogFooter>
          <AlertDialogCancel>Keep separate</AlertDialogCancel>
          <Button disabled={isSavingConfig} onClick={run}>
            {isSavingConfig && <Spinner />}
            Use Tier {picked} for all
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
